import { Component, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Footer } from './core/layout/footer/footer';
import { Menu } from './core/layout/menu/menu';
import { AuthService } from './shared/services/auth.service';
import { filter, Subscription } from 'rxjs';
import { ErrorModalComponent } from "./shared/components/error-modal/error-modal.component";
import { environment } from '../environments/environment';
import { UserDataModal } from "./shared/components/user-data-modal/user-data-modal";

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Footer, Menu, ErrorModalComponent, UserDataModal],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('Tú-Consultorio Empresarial');
  private authService = inject(AuthService);
  private router = inject(Router);
  routerSubscription: Subscription | null = null;

  showAlert = signal(false);
  messageAlert = signal('');
  hasNotVerify = signal(false);
  profileIncomplete = signal(false);
  needsGoogleCalendar = signal(false);

  isOpenUserModal = signal<boolean>(false);

  private isProfileIncompleteRoute(): boolean {
    const url = this.router.url;
    return url.includes('cart') || url.includes('appointment');
  }

  constructor() {
    effect(() => {
      const user = this.authService.currentUser();
      if (user) {
        if (!this.routerSubscription) {
          this.routerSubscription = this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
              if (!event.url.includes('/user/profile/edit')) {
                this.verifyIfUserProfileIsComplete();
              } else {
                this.showAlert.set(false);
              }
            });
        }
        
        if (!this.router.url.includes('/user/profile/edit')) {
          this.verifyIfUserProfileIsComplete();
        } else {
          this.showAlert.set(false);
        }
      } else {
        this.routerSubscription?.unsubscribe();
        this.routerSubscription = null;
      }
    });
  }

  async ngOnInit() {
    if (this.authService.getToken()) {
      try {
        const user = await this.authService.getUserData();
        this.authService.saveUserData(user);
      } catch (error) {
        // Interceptor handles 401 and logging out if refresh fails
        console.error('Failed to fetch user data on init', error);
      }
    }
  }

  verifyIfUserProfileIsComplete() {
    // email_verified_at
    const user = this.authService.getUser();

    if (user !== null && !user?.email_verified_at) {
      if (this.router.url.includes('/auth/email-verification')) {
        this.showAlert.set(false);
        return;
      }
      this.messageAlert.set('Tu correo electrónico no está verificado, por favor verifica tu correo electrónico para continuar.');
      this.hasNotVerify.set(true);
      this.showAlert.set(true);
      return;
    }
    
    const isComplete = this.authService.verifyIfUserProfileIsComplete();
    this.hasNotVerify.set(false);

    if (!isComplete) {
      this.profileIncomplete.set(true);
      this.messageAlert.set(
        'Para continuar con la programación es necesario contar con todos los datos de tu usuario.',
      );
      this.showAlert.set(true);
    } else {
      this.profileIncomplete.set(false);
    }

    // verify is user is advisor and has not connected his calendar
    if (user?.role?.name === 'advisor' && !user?.profile?.has_google_token) {
      this.needsGoogleCalendar.set(true);
      this.messageAlert.set(
        'Tu perfil necesita conectar Google Calendar para poder gestionar tus citas.',
      );
      this.showAlert.set(true);
    } else {
      this.needsGoogleCalendar.set(false);
    }
  }

  navigateToProfile() {
    this.showAlert.set(false);

    if (this.needsGoogleCalendar()) {
      this.connectGoogleCalendar();
      return;
    }

    if (this.profileIncomplete() && this.isProfileIncompleteRoute()) {
      this.isOpenUserModal.set(true);
      return;
    }
    
    if (!this.hasNotVerify()) {
      this.router.navigate(['/user/profile/edit']);
    }
  }

  async connectGoogleCalendar() {
    try {
      const response = await this.authService.getGoogleCalendarAuthUrl();
      window.location.href = response.url;
    } catch (ex: any) {
      console.error('Google Calendar connection failed:', ex);
    }
  }

  closeAlert() {
    this.showAlert.set(false);

    if (this.profileIncomplete() && this.isProfileIncompleteRoute()) {
      this.isOpenUserModal.set(true);
    }
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
  }
}
